import { useState, ReactElement, memo, useRef, useCallback } from 'react';
import AssistiveKeyboard from './keyboard';
import PmiTextField from 'shell/components/shared/text-field';
import { InputBaseProps } from '@mui/material';

interface InputKeyboardProps {
  value?: string;
  error?: string;
  touched?: boolean;
  onChange: (value: string, selection?: number) => void;
  disabled?: boolean;
  required?: boolean;
  defaultUppercase?: boolean;
  id: string;
  label: string;
  inputProps?: InputBaseProps['inputProps'];
}

const InputKeyboardComponent = ({
  value = '',
  onChange,
  error,
  touched,
  required,
  disabled = false,
  id,
  inputProps,
  label,
  defaultUppercase = false,
}: InputKeyboardProps): ReactElement => {
  const referenceInput = useRef<HTMLInputElement>();

  const [inputCursorPosition, setInputCursorPosition] = useState<number>(0);

  const saveCaretPosition = useCallback(() => {
    setInputCursorPosition(referenceInput.current?.selectionStart || value.length - 1);
  }, [setInputCursorPosition]);

  const onChangeKeyboard = useCallback(
    (characterSelected) => {
      const substringInitial = value.substring(0, inputCursorPosition);

      const substringFinal = value.substring(substringInitial.length);

      const newString = `${substringInitial}${characterSelected}${substringFinal}`;

      onChange(newString);
    },
    [onChange, value, inputCursorPosition]
  );

  const onBlurKeyboard = useCallback(() => {
    referenceInput.current?.focus();

    const position =
      value.length - 1 <= inputCursorPosition ? value.length - 1 : inputCursorPosition;

    referenceInput.current?.setSelectionRange(position, position, 'forward');

    setInputCursorPosition(position);
  }, [setInputCursorPosition, inputCursorPosition, value]);

  const onInputChange = useCallback(
    (event) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <div className="pmi-d-flex pmi-align-items-center" style={{ flex: 1 }}>
      <PmiTextField
        aria-label={label}
        label={required ? `${label} *` : label}
        id={id}
        name={id}
        fullWidth
        type="text"
        variant="outlined"
        value={value}
        inputRef={referenceInput}
        inputProps={inputProps}
        onClick={saveCaretPosition}
        onKeyUp={saveCaretPosition}
        onChange={onInputChange}
        error={touched && Boolean(error)}
        helperText={error}
        disabled={disabled}
      />
      <AssistiveKeyboard
        onChange={onChangeKeyboard}
        disabled={disabled}
        defaultUppercase={defaultUppercase}
        id={id}
        onBlur={onBlurKeyboard}
      />
    </div>
  );
};

InputKeyboardComponent.displayName = 'Input with assistive keyboard component';

const InputKeyboard = memo(InputKeyboardComponent);

export default InputKeyboard as typeof InputKeyboardComponent;
